import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const SECTIONS = [
    {
        id: "eligibility", num: "01", title: "Eligibility",
        body: [
            "Players must own a legitimate copy of the current Call of Duty title.",
            "Players must compete on their own account.",
            "Players must meet the tournament age requirement (18+ recommended for cash tournaments, or parental consent where legally required).",
            "False information during registration may result in immediate disqualification.",
        ],
    },
    {
        id: "format", num: "02", title: "Tournament Format",
        body: [
            "Single Elimination, Double Elimination, Round Robin, or Swiss (specified before registration).",
            "Match format will be announced before each tournament.",
            "Bracket seeding is final once the tournament begins.",
            "Tournament administrators have final authority over bracket adjustments.",
        ],
    },
    {
        id: "check-in", num: "03", title: "Match Check-In",
        lead: "Players must:",
        body: [
            "Check in before the scheduled start time.",
            "Join the correct lobby.",
            "Be ready within 10 minutes.",
        ],
        after: {
            lead: "Failure to appear may result in:",
            items: ["Match forfeiture", "Removal from the event"],
        },
    },
    {
        id: "platforms", num: "04", title: "Approved Platforms",
        lead: "Unless otherwise announced:",
        body: ["Xbox", "PlayStation", "PC"],
        note: "Crossplay settings will be announced before each event.",
    },
    {
        id: "integrity", num: "05", title: "Competitive Integrity",
        subsections: [
            {
                heading: "Cheating",
                lead: "Including but not limited to:",
                items: ["Aimbots", "Wallhacks", "ESP", "Radar hacks", "Trigger bots", "Recoil scripts", "Macros", "Any unauthorized software"],
                penalty: ["Permanent tournament ban", "Prize forfeiture"],
                penaltyLabel: "Immediate penalty:",
            },
            {
                heading: "Unauthorized Hardware",
                lead: "Not permitted:",
                items: ["Cronus Zen", "XIM", "ReaSnow", "Strike Pack mods", "Lag switches", "Any device providing an unfair competitive advantage"],
                penalty: ["Permanent ban"],
                penaltyLabel: "Penalty:",
            },
            {
                heading: "Account Sharing",
                lead: "No player may compete on another player's account.",
                penalty: ["Immediate disqualification", "Possible permanent suspension"],
                penaltyLabel: "Penalty:",
            },
            {
                heading: "Match Fixing",
                lead: "The following are prohibited:",
                items: ["Intentionally losing", "Paying another player to lose", "Bribery", "Collusion", "Prize splitting intended to manipulate results"],
                penalty: ["Lifetime ban"],
                penaltyLabel: "Penalty:",
            },
            {
                heading: "Stream Sniping",
                lead: "Players may not intentionally gain information from:",
                items: ["Opponent livestreams", "Discord broadcasts", "Spectator feeds"],
                penalty: ["Match forfeiture or ban"],
                penaltyLabel: "Penalty:",
            },
        ],
    },
    {
        id: "sportsmanship", num: "06", title: "Sportsmanship",
        lead: "Players may not:",
        body: ["Harass opponents", "Use hate speech", "Threaten others", "Spam voice or text chat", "Attempt to intimidate officials"],
        note: "Warnings may be issued. Repeated violations may result in removal.",
    },
    {
        id: "disconnects", num: "07", title: "Disconnects",
        paragraphs: [
            { heading: "Before First Blood / First Elimination:", body: "Restart if possible." },
            { heading: "After gameplay has significantly progressed:", body: "Tournament admins determine whether to replay or continue." },
        ],
        note: "Repeated disconnects are the player's responsibility.",
    },
    {
        id: "technical", num: "08", title: "Technical Issues",
        lead: "Players are responsible for:",
        body: ["Internet connection", "Console or PC performance", "Controllers", "Headsets", "Updates"],
        note: "Hardware failures generally do not justify a replay.",
    },
    {
        id: "recording", num: "09", title: "Recording Matches",
        lead: "Players are strongly encouraged to:",
        body: ["Record gameplay", "Save VODs", "Save screenshots"],
        after: {
            lead: "Tournament admins may request:",
            items: ["Gameplay footage", "Full-screen recordings", "Match history", "Game settings"],
        },
        note: "Failure to provide requested evidence may result in a ruling against the player.",
    },
    {
        id: "protests", num: "10", title: "Protests",
        lead: "All disputes must include:",
        body: ["Screenshot", "Video", "Timestamp", "Explanation"],
        note: "Protests must be submitted within 15 minutes after the match.",
    },
    {
        id: "prizes", num: "11", title: "Prize Payments",
        lead: "Players must:",
        body: ["Complete identity verification if requested.", "Provide accurate payment information."],
        after: {
            lead: "Prize payments may be withheld if:",
            items: ["An investigation is ongoing.", "Cheating is suspected.", "Rules were violated."],
        },
    },
    {
        id: "authority", num: "12", title: "Administrator Authority",
        lead: "Tournament administrators may:",
        body: ["Pause matches", "Restart matches", "Remove players", "Change schedules due to server issues", "Interpret rules"],
        note: "All administrator decisions are final.",
    },
    {
        id: "streaming", num: "13", title: "Streaming",
        paragraphs: [{ body: "Players may stream unless otherwise announced." }],
        lead: "Streams must not:",
        body: ["Reveal tournament passwords", "Reveal admin communications", "Encourage harassment"],
        note: "Tournament organizers may use player gameplay for promotional purposes.",
    },
    {
        id: "conduct", num: "14", title: "Tournament Conduct",
        lead: "Players agree to:",
        body: ["Follow all published rules.", "Follow administrator instructions.", "Respect opponents.", "Maintain competitive integrity."],
        note: "Failure to do so may result in penalties.",
    },
];

const PENALTY_TABLE = [
    ["Late Check-In", "Match Forfeit"],
    ["Unsportsmanlike Conduct", "Warning / DQ"],
    ["Harassment", "Suspension"],
    ["Cheating", "Permanent Ban"],
    ["Cronus / XIM / Unauthorized Devices", "Permanent Ban"],
    ["Account Sharing", "Permanent Ban"],
    ["Match Fixing", "Lifetime Ban"],
    ["Stream Sniping", "Match Loss or Ban"],
    ["False Identity", "Permanent Ban"],
];

const ANTI_CHEAT = [
    "Live Discord screen share",
    "Full desktop capture",
    "Task Manager review",
    "Running processes",
    "USB device inspection (where applicable)",
    "Match replay review",
];

const CASH_POLICY = [
    "All prize decisions are final after the investigation period.",
    "Players found cheating after payout may be permanently banned from future events.",
    "The organizer reserves the right to delay payouts while investigating credible reports.",
];

export default function Rules() {
    const [active, setActive] = useState("eligibility");

    useEffect(() => {
        const onScroll = () => {
            for (const s of SECTIONS) {
                const el = document.getElementById(s.id);
                if (!el) continue;
                const rect = el.getBoundingClientRect();
                if (rect.top < 200) setActive(s.id);
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="pt-[68px]" data-testid="rules-page">
            {/* Hero */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 pb-16">
                <div className="overline mb-6">[ Rulebook · v1.0 ]</div>
                <h1 className="font-display font-black tracking-tighter leading-[0.86] text-5xl md:text-[9vw]">
                    Official <br />
                    <span className="text-outline-strong">rulebook.</span>
                </h1>
                <p className="mt-8 text-secondary max-w-2xl text-base md:text-lg leading-relaxed">
                    Ranked Play Tournaments — the complete tournament rulebook. Every player agrees to these rules on registration.
                </p>
            </section>

            {/* Content grid */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32 grid md:grid-cols-12 gap-10">
                {/* Sticky TOC */}
                <aside className="hidden md:block md:col-span-3">
                    <div className="sticky top-28">
                        <div className="overline mb-5 text-white/40">/ Contents</div>
                        <nav className="space-y-2" data-testid="rules-toc">
                            {SECTIONS.map((s) => (
                                <a key={s.id} href={`#${s.id}`}
                                    data-testid={`toc-${s.id}`}
                                    className={`block font-mono text-[10px] tracking-[0.22em] uppercase transition-colors ${active === s.id ? "text-volt" : "text-white/50 hover:text-white"}`}>
                                    <span className="text-white/30 mr-2">{s.num}</span>{s.title}
                                </a>
                            ))}
                            <div className="pt-3 mt-3 border-t border-white/5 space-y-2">
                                <a href="#penalty-table" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 hover:text-white transition-colors">Penalty Table</a>
                                <a href="#anti-cheat" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 hover:text-white transition-colors">Anti-Cheat</a>
                                <a href="#cash-prize" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 hover:text-white transition-colors">Cash Prize</a>
                                <a href="#legal" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 hover:text-white transition-colors">Legal Disclaimer</a>
                            </div>
                        </nav>
                    </div>
                </aside>

                {/* Body */}
                <div className="md:col-span-9 space-y-20" data-testid="rules-body">
                    {SECTIONS.map((s, i) => <Section key={s.id} s={s} i={i} />)}

                    {/* Penalty table */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        id="penalty-table" className="scroll-mt-28" data-testid="penalty-table">
                        <ChapterHeader num="P" title="Penalty Table" />
                        <div className="border border-white/10 clip-frame overflow-hidden">
                            <div className="grid grid-cols-2 bg-[#0A0A0A] border-b border-white/10">
                                <div className="px-6 py-4 overline">Violation</div>
                                <div className="px-6 py-4 overline">Penalty</div>
                            </div>
                            {PENALTY_TABLE.map(([v, p], i) => (
                                <div key={v} className={`grid grid-cols-2 items-center ${i % 2 ? "bg-[#050505]" : "bg-[#0A0A0A]"} border-b border-white/5 last:border-b-0`}>
                                    <div className="px-6 py-4 font-body text-white">{v}</div>
                                    <div className="px-6 py-4 font-mono text-xs tracking-[0.16em] uppercase text-volt">{p}</div>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Anti-cheat */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.7 }} id="anti-cheat" className="scroll-mt-28" data-testid="anti-cheat">
                        <ChapterHeader num="A" title="Anti-Cheat Policy" />
                        <p className="text-white/70 mb-4 leading-relaxed">Tournament administrators may request:</p>
                        <BulletList items={ANTI_CHEAT} />
                        <NoteBox>Refusal to cooperate with a reasonable anti-cheat investigation may result in disqualification.</NoteBox>
                    </motion.div>

                    {/* Cash prize */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.7 }} id="cash-prize" className="scroll-mt-28" data-testid="cash-prize">
                        <ChapterHeader num="$" title="Cash Prize Policy" />
                        <BulletList items={CASH_POLICY} />
                    </motion.div>

                    {/* Legal */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.7 }} id="legal" className="scroll-mt-28 border-t border-white/10 pt-16" data-testid="legal">
                        <ChapterHeader num="§" title="Legal Disclaimer" />
                        <p className="text-white/70 leading-relaxed">
                            Ranked Play Tournaments is an independent tournament organizer and is not affiliated with, endorsed by, or sponsored by Activision Publishing, Inc. All Call of Duty trademarks are the property of their respective owners. Participants must also comply with Activision's Terms of Use and Security &amp; Enforcement Policies.
                        </p>
                        <p className="text-white/50 mt-6 text-sm leading-relaxed">
                            For cash tournaments, it's also a good idea to have a separate Terms &amp; Conditions document covering refunds, payment processing, taxes, eligibility by region, and liability, in addition to this competitive rulebook.
                        </p>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}

const ChapterHeader = ({ num, title }) => (
    <div className="mb-8">
        <div className="font-mono text-volt text-xs tracking-[0.32em] mb-3">[ {num} ]</div>
        <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl leading-[0.98]">{title}</h2>
    </div>
);

const Section = ({ s, i }) => (
    <motion.section
        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        id={s.id} className="scroll-mt-28"
        data-testid={`rule-section-${s.id}`}
    >
        <ChapterHeader num={s.num} title={s.title} />

        {s.paragraphs && s.paragraphs.map((p, i) => (
            <div key={i} className="mb-4">
                {p.heading && <div className="font-display font-extrabold text-white text-lg mb-1">{p.heading}</div>}
                <p className="text-white/70 leading-relaxed">{p.body}</p>
            </div>
        ))}

        {s.lead && <p className="text-white/70 leading-relaxed mb-4">{s.lead}</p>}
        {s.body && <BulletList items={s.body} />}

        {s.after && (
            <div className="mt-6">
                <p className="text-white/70 leading-relaxed mb-4">{s.after.lead}</p>
                <BulletList items={s.after.items} />
            </div>
        )}

        {s.subsections && (
            <div className="mt-4 space-y-10">
                {s.subsections.map((sub) => (
                    <div key={sub.heading} className="border-l-2 border-volt/60 pl-6" data-testid={`rule-sub-${sub.heading.toLowerCase().replace(/\s+/g,'-')}`}>
                        <div className="font-display font-extrabold text-white text-xl mb-3">{sub.heading}</div>
                        {sub.lead && <p className="text-white/70 leading-relaxed mb-3">{sub.lead}</p>}
                        {sub.items && <BulletList items={sub.items} />}
                        {sub.penalty && (
                            <div className="mt-5 border border-signal/30 bg-signal/[0.05] clip-frame-sm px-5 py-4">
                                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-signal mb-2">{sub.penaltyLabel}</div>
                                <ul className="space-y-1">
                                    {sub.penalty.map((p) => <li key={p} className="text-white text-sm">— {p}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        )}

        {s.note && <NoteBox>{s.note}</NoteBox>}
    </motion.section>
);

const BulletList = ({ items }) => (
    <ul className="space-y-2">
        {items.map((it) => (
            <li key={it} className="flex items-start gap-3 text-white/85">
                <span className="mt-[7px] w-1 h-1 bg-volt flex-shrink-0" />
                <span className="leading-relaxed">{it}</span>
            </li>
        ))}
    </ul>
);

const NoteBox = ({ children }) => (
    <div className="mt-6 border-l-2 border-white/20 pl-5 py-1 text-white/60 text-sm leading-relaxed italic">
        {children}
    </div>
);
